<template>
  <a-drawer
    :open="open"
    placement="right"
    :width="400"
    :closable="false"
    :mask="true"
    :mask-closable="true"
    :keyboard="true"
    :body-style="{
      padding: '20px 24px 30px',
      background: '#4a4c4e',
    }"
    :header-style="{
      padding: '0 20px',
      height: '50px',
      minHeight: '50px',
      maxHeight: '50px',
      background: '#57595b',
      borderBottom: '1px solid rgba(255,255,255,0.08)',
      display: 'flex',
      alignItems: 'center',
    }"
    :content-wrapper-style="{
      boxShadow: '-10px 0 30px rgba(0,0,0,0.24)',
    }"
    class="account-setting-drawer"
    @close="handleCancel"
  >
    <template #title>
      <div class="drawer-title">
        <img
          class="drawer-title__icon"
          :src="drawerShareIcon"
          :alt="t('accountSetting.title')"
        />
        <span>{{ t("accountSetting.title") }}</span>
      </div>
    </template>

    <template #extra>
      <button class="drawer-close" type="button" @click="handleCancel">
        <img :src="drawerCloseIcon" :alt="t('common.cancel')" />
      </button>
    </template>

    <a-spin :spinning="loading || avatarUploading" class="account-setting-spin">
      <div class="user-form-panel">
      <a-form
        ref="formRef"
        :model="formState"
        :rules="rules"
        layout="vertical"
        class="user-form"
        autocomplete="off"
      >
        <a-form-item
          :label="t('accountSetting.nicknameLabel')"
          name="nickname"
          class="user-form-item"
        >
          <a-input
            v-model:value="formState.nickname"
            allow-clear
            size="large"
            class="user-form-input"
            autocomplete="off"
          />
        </a-form-item>

        <div class="user-form-item user-form-item--avatar">
          <label class="ant-form-item-required user-form-item__label">
            {{ t("accountSetting.avatarLabel") }}
          </label>
          <a-upload
            :show-upload-list="false"
            accept="image/*"
            :before-upload="beforeAvatarUpload"
          >
            <button
              type="button"
              class="avatar-trigger"
              :class="{ 'avatar-trigger--empty': !avatarPreviewSrc }"
            >
              <img
                v-if="avatarPreviewSrc"
                :src="avatarPreviewSrc"
                :alt="t('accountSetting.avatarLabel')"
                class="avatar-trigger__image"
              />
              <div v-else class="avatar-trigger__placeholder">
                <PlusOutlined />
              </div>
            </button>
          </a-upload>
          <div v-if="avatarError" class="user-form-error">{{ avatarError }}</div>
        </div>

        <a-form-item
          :label="t('accountSetting.newPasswordLabel')"
          name="newPassword"
          class="user-form-item"
        >
          <a-input-password
            v-model:value="formState.newPassword"
            allow-clear
            size="large"
            class="user-form-input"
            autocomplete="new-password"
          >
            <template #iconRender="visible">
              <EyeInvisibleOutlined v-if="visible" />
              <EyeOutlined v-else />
            </template>
          </a-input-password>
        </a-form-item>

        <a-form-item
          :label="t('accountSetting.confirmNewPasswordLabel')"
          name="confirmPassword"
          class="user-form-item"
        >
          <a-input-password
            v-model:value="formState.confirmPassword"
            allow-clear
            size="large"
            class="user-form-input"
            autocomplete="new-password"
          >
            <template #iconRender="visible">
              <EyeInvisibleOutlined v-if="visible" />
              <EyeOutlined v-else />
            </template>
          </a-input-password>
        </a-form-item>

        <a-form-item
          :label="t('accountSetting.emailLabel')"
          name="email"
          class="user-form-item"
        >
          <a-input
            v-model:value="formState.email"
            type="email"
            disabled
            size="large"
            class="user-form-input"
            autocomplete="off"
          />
        </a-form-item>
        <div class="email-support-hint">
          {{ t("accountSetting.emailSupportHint") }}
        </div>

        <a-form-item
          :label="t('accountSetting.companyLabel')"
          name="company"
          class="user-form-item"
        >
          <a-input
            v-model:value="formState.company"
            disabled
            size="large"
            class="user-form-input"
            autocomplete="off"
          />
        </a-form-item>

        <a-form-item
          :label="t('accountSetting.industryLabel')"
          name="industry"
          class="user-form-item"
        >
          <a-select
            v-model:value="formState.industry"
            disabled
            size="large"
            class="user-form-select"
            popup-class-name="account-setting-industry-dropdown"
            :options="industryOptions"
          />
        </a-form-item>
      </a-form>

        <div class="drawer-actions">
          <div class="drawer-actions__right drawer-actions__right--single">
            <a-button
              class="drawer-action drawer-action--cancel"
              :disabled="submitLoading || loading || avatarUploading"
              @click="handleCancel"
            >
            {{ t("common.cancel") }}
            </a-button>
            <a-button
              type="primary"
              class="drawer-action drawer-action--confirm"
              :disabled="loading || avatarUploading"
              :loading="submitLoading"
              @click="handleConfirm"
            >
            {{ t("common.confirm") }}
            </a-button>
          </div>
        </div>
      </div>
    </a-spin>
  </a-drawer>
</template>

<script setup>
import { computed, onBeforeUnmount, reactive, ref, watch } from "vue";
import { message } from "ant-design-vue";
import {
  EyeInvisibleOutlined,
  EyeOutlined,
  PlusOutlined,
} from "@ant-design/icons-vue";
import { useI18n } from "vue-i18n";

import http from "@/api/http";
import { uploadAvatar } from "@/api/user";
import drawerCloseIcon from "@/assets/imgs/user/icon-drawer-close.png";
import drawerShareIcon from "@/assets/imgs/header/menu-account-icon.png";
import {
  isValidSpecEmail,
  isValidSpecPassword,
  isValidSpecUsernameOrEmail,
} from "@/utils/formValidation";

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  user: {
    type: Object,
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  submitLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["cancel", "confirm"]);

const { t } = useI18n();
const formRef = ref();
const avatarError = ref("");
const avatarUploading = ref(false);
const avatarPreviewUrl = ref("");
const AVATAR_MAX_SIZE = 5 * 1024 * 1024;

const buildProfileIconUrl = (profileIcon) =>
  profileIcon ? `/api/v1/download/${profileIcon}` : "";

const extractUploadedFileId = (response) => {
  if (typeof response === "string") {
    return response;
  }

  if (typeof response?.data === "string") {
    return response.data;
  }

  return "";
};

const buildDefaultForm = () => ({
  nickname: "",
  profileIcon: "",
  newPassword: "",
  confirmPassword: "",
  email: "",
  company: "",
  industry: undefined,
});

const formState = reactive(buildDefaultForm());
const avatarPreviewSrc = computed(
  () => avatarPreviewUrl.value || "",
);

const revokeAvatarPreviewUrl = () => {
  if (avatarPreviewUrl.value?.startsWith("blob:")) {
    URL.revokeObjectURL(avatarPreviewUrl.value);
  }
  avatarPreviewUrl.value = "";
};

const loadAvatarPreview = async (profileIcon) => {
  revokeAvatarPreviewUrl();
  if (!profileIcon) {
    return;
  }

  try {
    const response = await http.get(`/api/v1/download/${profileIcon}`, {
      responseType: "blob",
    });
    const blob = response?.data instanceof Blob
      ? response.data
      : new Blob([response?.data]);

    if (!blob.size) {
      return;
    }

    avatarPreviewUrl.value = URL.createObjectURL(blob);
  } catch (_error) {
    avatarPreviewUrl.value = "";
  }
};

const industryOptions = computed(() => {
  const presetOptions = [
    {
      value: "surveying-and-mapping",
      label: t("accountSetting.industryOptions.surveyingMapping"),
    },
    {
      value: "forestry-and-ecology",
      label: t("accountSetting.industryOptions.forestryEcology"),
    },
    {
      value: "smart-city",
      label: t("accountSetting.industryOptions.smartCity"),
    },
  ];

  if (
    formState.industry &&
    !presetOptions.some((option) => option.value === formState.industry)
  ) {
    return [
      {
        value: formState.industry,
        label: formState.industry,
      },
      ...presetOptions,
    ];
  }

  return presetOptions;
});

const rules = computed(() => ({
  nickname: [
    {
      validator: async (_rule, value) => {
        const nickname = String(value ?? "").trim();

        if (!nickname) {
          return Promise.reject(t("accountSetting.rules.nicknameRequired"));
        }

        if (!isValidSpecUsernameOrEmail(nickname)) {
          return Promise.reject(t("accountSetting.rules.nicknameInvalid"));
        }

        return Promise.resolve();
      },
      trigger: ["blur", "change"],
    },
  ],
  newPassword: [
    {
      validator: async (_rule, value) => {
        if (!value) {
          return Promise.resolve();
        }

        if (String(value).trim().length === 0) {
          return Promise.reject(t("accountSetting.rules.newPasswordRequired"));
        }

        if (!isValidSpecPassword(value)) {
          return Promise.reject(t("accountSetting.rules.newPasswordInvalid"));
        }

        return Promise.resolve();
      },
      trigger: ["blur", "change"],
    },
  ],
  confirmPassword: [
    {
      validator: async (_rule, value) => {
        if (!formState.newPassword && !value) {
          return Promise.resolve();
        }
        if (formState.newPassword && !value) {
          return Promise.reject(t("accountSetting.rules.confirmNewPasswordRequired"));
        }
        if (value !== formState.newPassword) {
          return Promise.reject(t("accountSetting.rules.confirmNewPasswordMismatch"));
        }
        return Promise.resolve();
      },
      trigger: ["blur", "change"],
    },
  ],
  email: [
    {
      validator: async (_rule, value) => {
        const email = String(value ?? "").trim();

        if (!email) {
          return Promise.reject(t("accountSetting.rules.emailRequired"));
        }

        if (!isValidSpecEmail(email)) {
          return Promise.reject(t("accountSetting.rules.emailInvalid"));
        }

        return Promise.resolve();
      },
      trigger: ["blur", "change"],
    },
  ],
}));

watch(
  () => formState.newPassword,
  () => {
    if (props.open && formState.confirmPassword) {
      formRef.value?.validateFields?.(["confirmPassword"]).catch(() => {});
    }
  },
);

watch(
  () => [props.open, props.user],
  async ([open]) => {
    if (!open) {
      revokeAvatarPreviewUrl();
      return;
    }

    const nextState = buildDefaultForm();

    if (props.user) {
      nextState.profileIcon = props.user.profileIcon || "";
      nextState.nickname = props.user.nickname || props.user.username || "";
      nextState.email = props.user.email || "";
      nextState.company = props.user.company || "";
      nextState.industry = props.user.industry || "";
    }

    Object.assign(formState, nextState);
    avatarError.value = "";
    avatarUploading.value = false;
    formRef.value?.clearValidate?.();
    await loadAvatarPreview(nextState.profileIcon);
  },
  { immediate: true },
);

const beforeAvatarUpload = async (file) => {
  if (!file.type?.startsWith("image/")) {
    avatarError.value = t("accountSetting.avatarImageOnly");
    message.error(avatarError.value);
    return false;
  }

  if (file.size > AVATAR_MAX_SIZE) {
    avatarError.value = t("accountSetting.avatarTooLarge");
    message.error(avatarError.value);
    return false;
  }

  avatarError.value = "";
  avatarUploading.value = true;

  try {
    const response = await uploadAvatar(file);
    const profileIcon = extractUploadedFileId(response);

    if (!profileIcon) {
      message.error(t("accountSetting.uploadFailed"));
      return false;
    }

    formState.profileIcon = profileIcon;
    await loadAvatarPreview(profileIcon);
    avatarError.value = "";
    message.success(t("accountSetting.uploadSuccess"));
  } catch (_error) {
    console.error("[AccountSettingDrawer] Failed to upload avatar:", _error);
    // HTTP拦截器已处理错误提示
  } finally {
    avatarUploading.value = false;
  }

  return false;
};

const validateAvatar = () => {
  if (formState.profileIcon || avatarPreviewUrl.value) {
    avatarError.value = "";
    return true;
  }
  avatarError.value = t("accountSetting.rules.avatarRequired");
  return false;
};

const handleCancel = () => {
  emit("cancel");
};

const handleConfirm = async () => {
  const avatarValid = validateAvatar();

  try {
    await formRef.value?.validate();
  } catch (_error) {
    return;
  }

  if (!avatarValid) {
    return;
  }

  emit("confirm", {
    nickname: formState.nickname.trim(),
    profileIcon: formState.profileIcon,
    avatarUrl: buildProfileIconUrl(formState.profileIcon),
    password: formState.newPassword.trim(),
    newPassword: formState.newPassword.trim(),
    confirmPassword: formState.confirmPassword.trim(),
    email: formState.email.trim(),
    company: formState.company,
    industry: formState.industry,
  });
};

onBeforeUnmount(() => {
  revokeAvatarPreviewUrl();
});
</script>

<style scoped lang="less">
.drawer-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 50px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
}

.drawer-title__icon {
  width: 20px;
  height: 20px;
  flex: none;
}

.drawer-close {
  width: 20px;
  height: 20px;
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;

  img {
    display: block;
    width: 12px;
    height: 12px;
    margin: 4px;
  }
}

.user-form-panel {
  min-height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
}

.account-setting-spin {
  width: 100%;
}

.user-form {
  flex: 1;
}

.user-form-item {
  margin-bottom: 18px;
}

.user-form-item--avatar {
  margin-bottom: 24px;
}

.user-form-item__label,
:deep(.user-form .ant-form-item-label > label) {
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
  font-weight: 700;
}

.user-form-item__label {
  display: block;
  margin-bottom: 8px;
  line-height: 16px;
}

.user-form-item__label.ant-form-item-required::before {
  display: inline-block;
  margin-inline-end: 4px;
  color: #ff4d4f;
  font-size: 14px;
  font-family: SimSun, sans-serif;
  line-height: 1;
  content: "*";
}

:deep(.user-form .ant-form-item-label) {
  padding-bottom: 8px;
}

:deep(.user-form .ant-input),
:deep(.user-form .ant-input-affix-wrapper),
:deep(.user-form .ant-select-selector) {
  height: 32px !important;
  border-radius: 4px !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  background: rgba(0, 0, 0, 0.12) !important;
  color: #ffffff !important;
  box-shadow: none !important;
}

:deep(.user-form .ant-input[disabled]),
:deep(.user-form .ant-input-affix-wrapper-disabled),
:deep(.user-form .ant-select-disabled .ant-select-selector) {
  color: rgba(255, 255, 255, 0.72) !important;
  background: rgba(0, 0, 0, 0.08) !important;
  cursor: not-allowed !important;
}

:deep(.user-form .ant-input) {
  padding: 4px 11px;
  line-height: 22px;
}

:deep(.user-form .ant-input-affix-wrapper) {
  padding: 0 11px !important;
}

:deep(.user-form .ant-input-affix-wrapper > input.ant-input) {
  height: 30px !important;
  padding: 0 !important;
  border: none !important;
  outline: none !important;
  background: transparent !important;
  color: #ffffff !important;
  box-shadow: none !important;
}

:deep(.user-form .ant-input-affix-wrapper .ant-input) {
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
}

:deep(.user-form .ant-input-affix-wrapper .ant-input:focus),
:deep(.user-form .ant-input-affix-wrapper .ant-input-focused),
:deep(.user-form .ant-input-affix-wrapper .ant-input-status-error) {
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
}

:deep(.user-form .ant-input-clear-icon),
:deep(.user-form .ant-select-clear) {
  color: rgba(255, 255, 255, 0.45);
  background: transparent !important;
}

:deep(.user-form .ant-input-password-icon) {
  color: rgba(255, 255, 255, 0.45);
}

:deep(.user-form .ant-input:focus),
:deep(.user-form .ant-input-affix-wrapper:focus),
:deep(.user-form .ant-input-affix-wrapper-focused),
:deep(.user-form .ant-input-focused),
:deep(.user-form .ant-select-focused .ant-select-selector),
:deep(.user-form .ant-input-affix-wrapper:hover),
:deep(.user-form .ant-select-selector:hover) {
  border-color: #04a94d !important;
}

:deep(.user-form .ant-select-selection-item),
:deep(.user-form .ant-select-arrow) {
  color: #ffffff !important;
}

:deep(.user-form .ant-select-selector) {
  display: flex !important;
  align-items: center !important;
}

:deep(.user-form .ant-select-selection-wrap) {
  display: flex !important;
  align-items: center !important;
}

:deep(.user-form .ant-select-selection-item) {
  display: flex !important;
  align-items: center !important;
  line-height: 30px !important;
}

:deep(.user-form .ant-form-item-has-error .ant-input),
:deep(.user-form .ant-form-item-has-error .ant-input-affix-wrapper),
:deep(.user-form .ant-form-item-has-error .ant-select-selector),
:deep(.user-form .ant-input-status-error:not(.ant-input-disabled)),
:deep(.user-form .ant-input-affix-wrapper-status-error:not(.ant-input-affix-wrapper-disabled)) {
  border-color: #ff5d57 !important;
  background: rgba(0, 0, 0, 0.12) !important;
}

:deep(.user-form .ant-form-item-explain-error) {
  color: #ff6b6b;
  font-size: 12px;
  margin-top: 4px;
}

.avatar-trigger {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.04);
  padding: 0;
  overflow: hidden;
  cursor: pointer;
}

.avatar-trigger--empty {
  border-style: dashed;
}

.avatar-trigger__image {
  width: 60px;
  height: 60px;
  display: block;
  border-radius: 50%;
  object-fit: cover;
}

.avatar-trigger__placeholder {
  width: 100%;
  height: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.76);
  font-size: 18px;
}

.user-form-error {
  color: #ff6b6b;
  font-size: 12px;
  line-height: 18px;
  margin-top: 6px;
}

.email-support-hint {
  margin: -8px 0 18px;
  color: #ff5d57;
  font-size: 12px;
  line-height: 18px;
}

.drawer-actions {
  display: flex;
  justify-content: flex-end;
  width: 340px;
  margin-top: auto;
  padding-top: clamp(24px, 4vh, 64px);
}

.drawer-actions__right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.drawer-actions__right--single {
  margin-left: auto;
}

.drawer-action {
  height: 32px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 700;
  box-shadow: none;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

:deep(.drawer-action.ant-btn) {
  height: 32px;
  border-radius: 4px;
  font-size: 14px;
  font-family: Arial-BoldMT, Arial, sans-serif;
  font-weight: 700;
  line-height: 16px;
  padding: 0;
  box-shadow: none;
}

.drawer-action--cancel {
  width: 72px;
}

.drawer-action--confirm {
  width: 68px;
}

:deep(.drawer-action--cancel.ant-btn) {
  border: 1px solid #04a94d;
  color: #04a94d;
  background: rgba(0, 0, 0, 0.08);
}

:deep(.drawer-action--cancel.ant-btn:hover),
:deep(.drawer-action--cancel.ant-btn:focus) {
  border-color: #04a94d;
  color: #04a94d;
  background: rgba(4, 169, 77, 0.08);
}

:deep(.drawer-action--confirm.ant-btn),
:deep(.drawer-action--confirm.ant-btn-primary) {
  border: 1px solid #04a94d;
  color: #ffffff;
  background: #04a94d;
}

:deep(.drawer-action--confirm.ant-btn:hover),
:deep(.drawer-action--confirm.ant-btn:focus),
:deep(.drawer-action--confirm.ant-btn-primary:hover),
:deep(.drawer-action--confirm.ant-btn-primary:focus) {
  border-color: #04a94d;
  color: #ffffff;
  background: #04a94d;
}

:deep(.account-setting-drawer .ant-drawer-content),
:deep(.account-setting-drawer .ant-drawer-header) {
  background: #4a4c4e;
}

:deep(.account-setting-drawer .ant-drawer-header) {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

:deep(.account-setting-drawer .ant-drawer-header-title) {
  height: 50px;
  display: flex;
  align-items: center;
}

:deep(.account-setting-drawer .ant-drawer-title) {
  display: flex;
  align-items: center;
  line-height: 1;
}

:deep(.account-setting-drawer .ant-drawer-mask) {
  background: rgba(0, 0, 0, 0.45);
}

:deep(.account-setting-industry-dropdown .ant-select-item) {
  color: #ffffff;
  background: #4a4c4e;
}

:deep(.account-setting-industry-dropdown .ant-select-item-option-selected) {
  background: rgba(4, 169, 77, 0.18);
}

:deep(.account-setting-industry-dropdown .ant-select-item-option-active) {
  background: rgba(255, 255, 255, 0.08);
}

:deep(.account-setting-industry-dropdown) {
  background: #4a4c4e;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

@media (max-height: 820px) {
  .drawer-actions {
    padding-top: 20px;
  }
}
</style>
